/* Non-modal theme notifications. No order/cart writes, tracking pixels or invented activity. */
if (!customElements.get('rw-notifications')) {
  customElements.define('rw-notifications', class extends HTMLElement {
    connectedCallback() {
      if (this.events && !this.events.signal.aborted) return;
      this.events = new AbortController();
      this.frame = this.querySelector('[data-notice-frame]');
      this.content = this.querySelector('[data-notice-content]');
      this.live = this.querySelector('[data-notice-live]');
      if (!this.frame || !this.content) return;
      this.editor = this.dataset.editor === 'true';
      this.screen = matchMedia('(max-width: 767px)');
      this.templates = [...this.querySelectorAll('template[data-notification]')];
      this.items = this.dataset.mode === 'feed' ? [] : this.templates.map(template => ({
        id: 'block:' + template.dataset.id, type: template.dataset.kind,
        occurred_at: template.dataset.time, eligible: template.dataset.eligible === 'true', template,
      })).filter(item => this.valid(item));
      if (this.dataset.shuffle === 'true') this.shuffle(this.items);
      this.index = 0; this.count = 0; this.hovered = false; this.focused = false; this.previousFocus = null;
      const storedEvents = this.readSession('events');
      this.seen = new Set(Array.isArray(storedEvents) ? storedEvents.filter(value => typeof value === 'string').slice(-100) : []);
      this.phase = 'waiting'; this.remaining = this.seconds('delay', 6);
      this.stopped = !this.editor && (this.dataset.enabled !== 'true' || this.dataset.permitted !== 'true' || this.readSession('dismissed') === true);
      const options = { signal: this.events.signal };
      this.querySelector('[data-notice-close]').addEventListener('click', () => this.close(), options);
      this.frame.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') { this.hovered = true; this.sync(); } }, options);
      this.frame.addEventListener('pointerleave', () => { this.hovered = false; this.sync(); }, options);
      this.frame.addEventListener('focusin', event => {
        if (!this.frame.contains(event.relatedTarget)) this.previousFocus = event.relatedTarget;
        this.focused = true; this.sync();
      }, options);
      this.frame.addEventListener('focusout', () => queueMicrotask(() => {
        if (this.isConnected) { this.focused = this.frame.contains(document.activeElement); this.sync(); }
      }), options);
      this.frame.addEventListener('keydown', event => {
        if (event.key === 'Escape') { event.stopPropagation(); event.preventDefault(); this.close(); }
      }, options);
      document.addEventListener('visibilitychange', () => this.sync(), options);
      this.screen.addEventListener('change', () => this.sync(), options);
      if (this.dataset.pauseOverlays === 'true') {
        // Observe open state only; audio progress, price updates and animation styles do not trigger this observer.
        this.overlays = new MutationObserver(() => this.sync());
        this.overlays.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] });
        for (const event of ['drawer:handleAfterShow', 'drawer:handleAfterHide', 'modal:handleAfterShow', 'modal:handleAfterHide']) document.addEventListener(event, () => this.sync(), options);
      }
      if (this.editor) {
        const section = this.closest('.shopify-section') || this.parentElement;
        section.addEventListener('shopify:block:select', event => this.preview(this.templates.find(t => t.dataset.id === event.detail?.blockId)), options);
        section.addEventListener('shopify:section:select', () => this.preview(this.templates[0]), options);
        section.addEventListener('shopify:section:deselect', () => { this.frame.hidden = true; this.dataset.visible = 'false'; }, options);
        if (this.dataset.enabled === 'true') {
          this.preview(this.templates[0]);
          if (this.dataset.mode !== 'manual' && this.dataset.feed) this.loadFeed();
        }
        return;
      }
      if (!this.stopped && this.dataset.mode !== 'manual' && this.dataset.feed) this.loadFeed();
      this.sync();
    }
    disconnectedCallback() {
      queueMicrotask(() => {
        if (this.isConnected) return;
        this.clearTimer(); this.events?.abort(); this.overlays?.disconnect(); this.feedRequest?.abort();
        clearTimeout(this.feedTimeout); if (this.frame) this.frame.hidden = true;
      });
    }
    seconds(key, fallback) {
      const n = Number(this.dataset[key]); return Math.max(0, Math.min(120, Number.isFinite(n) ? n : fallback)) * 1000;
    }
    readSession(kind) {
      if (this.editor) return null;
      try { return JSON.parse(sessionStorage.getItem('rw-notices:' + this.dataset.section + ':' + kind)); } catch (_) { return null; }
    }
    saveSession(kind, value) {
      if (this.editor) return;
      try { sessionStorage.setItem('rw-notices:' + this.dataset.section + ':' + kind, JSON.stringify(value)); } catch (_) { /* Storage is optional; per-page controls still work. */ }
    }
    shuffle(items) {
      for (let i = items.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [items[i], items[j]] = [items[j], items[i]]; }
    }
    purchaseTime(value) {
      // A real, explicit zoned timestamp is required. Never replace missing data with the current time.
      if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})$/.test(value)) return NaN;
      return Date.parse(value);
    }
    valid(item) {
      if (!item.eligible || !['message', 'product', 'review', 'purchase', 'statistic'].includes(item.type)) return false;
      if (item.template && !item.template.content.querySelector('[data-notice-text]')?.textContent.trim()) return false;
      if (!item.template && !(item.title || item.message || (item.link && item.link_label))) return false;
      if (item.type === 'purchase') {
        const time = this.purchaseTime(item.occurred_at), age = Date.now() - time;
        if (!Number.isFinite(time) || age < 0 || age > Number(this.dataset.days || 7) * 86400000) return false;
      }
      return true;
    }
    eventKey(item) { return item.id + ':' + item.occurred_at; }
    nextItem() {
      const length = this.items.length;
      for (let attempt = 0; attempt < length; attempt++) {
        if (this.index >= length) {
          if (this.dataset.repeat !== 'true') return null;
          this.index = 0;
        }
        const item = this.items[this.index++];
        if (this.valid(item) && (item.type !== 'purchase' || !this.seen.has(this.eventKey(item)))) return item;
      }
      return null;
    }
    hardBlocked() {
      return document.hidden || (this.screen.matches ? this.dataset.mobile !== 'true' : this.dataset.desktop !== 'true') ||
        (this.dataset.pauseOverlays === 'true' && !!document.querySelector('.drawer[open], .modal[open], dialog[open]'));
    }
    clearTimer() {
      if (this.timer !== undefined) { clearTimeout(this.timer); this.timer = undefined; this.remaining = Math.max(0, this.deadline - performance.now()); }
    }
    sync() {
      if (this.editor || !this.isConnected) return;
      const blocked = this.stopped || this.hardBlocked() || this.phase === 'finished';
      this.frame.hidden = blocked || this.phase !== 'showing';
      this.dataset.visible = String(!this.frame.hidden);
      if (blocked || this.hovered || this.focused) { this.clearTimer(); return; }
      if (this.timer === undefined) {
        this.deadline = performance.now() + this.remaining;
        this.timer = setTimeout(() => { this.timer = undefined; this.advance(); }, this.remaining);
      }
    }
    advance() {
      if (this.stopped || !this.isConnected) return;
      if (this.hardBlocked()) { this.remaining = 0; this.sync(); return; }
      if (this.phase === 'showing') {
        this.frame.hidden = true; this.dataset.visible = 'false';
        this.phase = this.count >= Number(this.dataset.max || 5) ? 'finished' : 'waiting';
        this.remaining = this.seconds('interval', 20);
      } else {
        const item = this.count < Number(this.dataset.max || 5) ? this.nextItem() : null;
        if (!item) { this.phase = 'finished'; this.sync(); return; }
        this.render(item); this.count++;
        if (item.type === 'purchase') {
          this.seen.add(this.eventKey(item)); this.saveSession('events', [...this.seen].slice(-100));
        }
        this.phase = 'showing'; this.remaining = this.seconds('duration', 8);
      }
      this.sync();
    }
    render(item) {
      this.content.replaceChildren(item.template ? item.template.content.cloneNode(true) : this.feedContent(item));
      this.current = item;
      const time = this.content.querySelector('[data-notice-time]');
      if (time && item.type === 'purchase') {
        const timestamp = this.purchaseTime(item.occurred_at);
        if (Number.isFinite(timestamp)) {
          const date = new Date(timestamp); time.dateTime = date.toISOString();
          time.textContent = this.relativeTime(timestamp); time.title = date.toLocaleString(document.documentElement.lang || undefined); time.hidden = false;
        }
      }
      if (this.live) this.live.textContent = this.content.textContent.replace(/\s+/g, ' ').trim();
    }
    relativeTime(timestamp) {
      const locale = document.documentElement.lang || this.dataset.language || 'en';
      const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
      const unit = seconds >= 86400 ? 'day' : seconds >= 3600 ? 'hour' : seconds >= 60 ? 'minute' : 'second';
      const value = Math.floor(seconds / (unit === 'day' ? 86400 : unit === 'hour' ? 3600 : unit === 'minute' ? 60 : 1));
      try { return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(-value, unit); }
      catch (_) { return new Date(timestamp).toLocaleString(locale); }
    }
    close() {
      this.clearTimer(); this.frame.hidden = true; this.dataset.visible = 'false';
      if (this.live) this.live.textContent = '';
      if (this.previousFocus?.isConnected && this.previousFocus !== document.body) this.previousFocus.focus({ preventScroll: true });
      this.hovered = false; this.focused = false;
      if (this.editor) return;
      if (this.dataset.close === 'next') {
        this.phase = this.count >= Number(this.dataset.max || 5) ? 'finished' : 'waiting';
        this.remaining = this.seconds('interval', 20); this.sync();
      } else {
        this.stopped = true;
        if (this.dataset.close === 'session') this.saveSession('dismissed', true);
      }
    }
    preview(template) {
      if (!this.editor) return;
      if (template?.content.querySelector('[data-notice-text]')?.textContent.trim()) this.render({ template, id: template.dataset.id, type: template.dataset.kind, occurred_at: template.dataset.time });
      else this.content.replaceChildren(this.querySelector('[data-notice-placeholder]').content.cloneNode(true));
      this.frame.hidden = false; this.dataset.visible = 'true';
    }
    safeUrl(value, image = false) {
      if (typeof value !== 'string' || value.length > 2048) return '';
      try {
        const url = new URL(value, location.origin);
        if (url.username || url.password || (url.protocol !== 'https:' && !(url.protocol === 'http:' && url.origin === location.origin))) return '';
        if (image && url.origin !== location.origin && url.hostname !== 'cdn.shopify.com' && !url.hostname.endsWith('.shopifycdn.com')) return '';
        return url.href;
      } catch (_) { return ''; }
    }
    filterCodes(list, current) {
      if (list === undefined || (Array.isArray(list) && !list.length)) return true;
      return Array.isArray(list) && list.some(code => typeof code === 'string' && code.toLowerCase() === current.toLowerCase());
    }
    productUrl(value) {
      const safe = this.safeUrl(value);
      if (!safe) return '';
      const url = new URL(safe);
      const root = this.dataset.root || '/';
      if (url.origin === location.origin && url.pathname.startsWith('/products/') && /^\/(?:[a-zA-Z0-9-]+\/)?$/.test(root)) url.pathname = root + url.pathname.slice(1);
      return url.href;
    }
    normalizeFeed(raw) {
      if (!raw || typeof raw !== 'object' || typeof raw.id !== 'string' || !raw.id.trim() || raw.id.length > 160) return null;
      const text = key => typeof raw[key] === 'string' ? raw[key].slice(0, 600) : '';
      const item = { id: 'feed:' + raw.id, type: raw.type, title: text('title'), message: text('message'), footer: text('footer'),
        customerName: text('customer_name'), location: text('location'),
        link: this.productUrl(raw.product_url || raw.link_url), link_label: text('link_label'), image: this.safeUrl(raw.image_url, true),
        occurred_at: text('occurred_at'), rating: Math.max(0, Math.min(5, Math.floor(Number(raw.rating) || 0))),
        eligible: this.filterCodes(raw.countries, this.dataset.country) && this.filterCodes(raw.languages, this.dataset.language) &&
          this.filterCodes(raw.page_types, this.dataset.page) && this.filterCodes(raw.product_handles, this.dataset.product || ''),
      };
      return this.valid(item) ? item : null;
    }
    async loadFeed() {
      const url = this.safeUrl(this.dataset.feed);
      if (!url) return;
      const endpoint = new URL(url);
      endpoint.searchParams.set('country', this.dataset.country); endpoint.searchParams.set('locale', this.dataset.language);
      const controller = this.feedRequest = new AbortController();
      const timeout = this.feedTimeout = setTimeout(() => controller.abort(), 5000);
      try {
        const response = await fetch(endpoint.href, { signal: controller.signal, credentials: 'omit', headers: { Accept: 'application/json' } });
        if (!response.ok || !response.headers.get('content-type')?.includes('application/json') || Number(response.headers.get('content-length')) > 262144) {
          await response.body?.cancel(); return;
        }
        let raw;
        if (response.body?.getReader) {
          const reader = response.body.getReader(), decoder = new TextDecoder(); let size = 0, source = '';
          try {
            while (true) { const { value, done } = await reader.read(); if (done) break; size += value.byteLength; if (size > 262144) { await reader.cancel(); return; } source += decoder.decode(value, { stream: true }); }
            source += decoder.decode(); raw = JSON.parse(source);
          } finally { reader.releaseLock(); }
        } else {
          const source = await response.text(); if (source.length > 262144) return; raw = JSON.parse(source);
        }
        if (controller.signal.aborted || this.events.signal.aborted || !this.isConnected || raw.version !== 1 || !Array.isArray(raw.notifications)) return;
        const ids = new Set(this.items.map(item => item.id));
        const added = raw.notifications.slice(0, 100).map(item => this.normalizeFeed(item)).filter(item => {
          if (!item || ids.has(item.id)) return false; ids.add(item.id); return true;
        });
        if (this.dataset.shuffle === 'true') this.shuffle(added);
        this.items.push(...added);
        if (this.editor && added.length && (this.dataset.mode === 'feed' || !this.templates.length)) {
          this.render(added[0]); this.frame.hidden = false; this.dataset.visible = 'true';
          return; // Editor preview never starts timers or records visitor exposure.
        }
        if (added.length && this.phase === 'finished' && this.count < Number(this.dataset.max || 5)) { this.phase = 'waiting'; this.remaining = this.seconds('delay', 6); }
        this.sync();
      } catch (_) { /* Feed failure never interrupts shopping or generates substitute customer activity. */ }
      finally { clearTimeout(timeout); }
    }
    feedContent(item) {
      const make = (tag, className, text) => { const node = document.createElement(tag); node.className = className; if (text) node.textContent = text; return node; };
      const article = make('article', 'rw-notices__content'); article.dataset.noticeKind = item.type;
      if (item.image) {
        const media = make('div', 'rw-notices__media'), image = document.createElement('img');
        image.addEventListener('error', () => media.remove(), { once: true, signal: this.events.signal });
        image.src = item.image; image.alt = ''; image.width = 96; image.height = 96; image.loading = 'lazy'; media.setAttribute('aria-hidden', 'true'); media.append(image); article.append(media);
      }
      const body = make('div', 'rw-notices__body'); article.append(body);
      const purchase = item.type === 'purchase';
      if (purchase) {
        const sentence = make('p', 'rw-notices__purchase');
        sentence.append(make('span', 'rw-notices__customer', item.customerName || item.title));
        sentence.append(document.createTextNode(' ' + (this.dataset.purchaseAction || this.dataset.labelPurchase || '') + ' '));
        if (item.link_label) {
          const product = make(item.link ? 'a' : 'span', 'rw-notices__link', item.link_label);
          if (item.link) product.href = item.link;
          sentence.append(product);
        }
        body.append(sentence);
        if (item.location) body.append(make('p', 'rw-notices__location', item.location));
      } else {
        const label = this.dataset['label' + item.type[0].toUpperCase() + item.type.slice(1)];
        body.append(make('span', 'rw-notices__kind', label));
        if (item.title) body.append(make('p', 'rw-notices__title', item.title));
      }
      if (item.message) body.append(make('p', 'rw-notices__message', item.message));
      if (!purchase && item.link && item.link_label) { const link = make('a', 'rw-notices__link', item.link_label); link.href = item.link; body.append(link); }
      if (item.type === 'review' && item.rating) {
        // Use text for an accessible rating; never label an unverified feed entry as verified.
        body.append(make('span', 'rw-notices__rating', item.rating + ' / 5 ★'));
      }
      if (item.footer) body.append(make('p', 'rw-notices__footer', item.footer));
      const time = make('time', 'rw-notices__time'); time.dataset.noticeTime = ''; time.hidden = true; body.append(time);
      return article;
    }
  });
}
